import isDate from 'lodash/isDate';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import intlInstanceCache from './intlInstanceCache';
import { FormatterOptions } from '../models/configuration/Formatter';
import FormatNumberOptions from '../models/utils/FormatNumberOptions';
import FormatDateOpitons from '../models/utils/FormatDateOptions';
import { NumberThresholds } from '../models/configuration/NumberThresholds';
import { sortBy } from '@kurtosys/ksys-app-template/dist/utils/collection';
import { translate } from '../Components/Translate';

export const padStart = (value: any, length: number, padder: string = '0') => {
    if (value !== undefined && value !== null) {
        let response: string = value.toString();
        if (response.length < length) {
            const split = response.split('');
            while (split.length < length) {
                split.unshift(padder);
            }
            response = split.join('');
        }
        return response;
    }
    return '';
};

export const formatNumber = (formatNumberOptions: FormatNumberOptions) => {
    let { value } = formatNumberOptions;
    const { locale, options, dataStore } = formatNumberOptions;

    if (isNumber(value)) {
        try {
            const { numberThresholds, thresholdSeparator = '' } = options;

            let numberThreshold: NumberThresholds | undefined;
            if (numberThresholds) {
                const sortedNumberThresholds = sortBy(
                    numberThresholds,
                    item => item.value.toString(),
                    'DESC'
                );
                for (const numberGroup of sortedNumberThresholds) {
                    const { value: thresholdValue } = numberGroup;
                    if (Math.abs(value) >= thresholdValue) {
                        numberThreshold = numberGroup;
                        value = value / thresholdValue;
                        break;
                    }
                }
            }
            const formattedNumber = intlInstanceCache
                .getNumber({ locale, options, dataStore })
                .format(value);
            if (numberThreshold) {
                const {
                    text,
                    separator = thresholdSeparator,
                    displayPosition = 'suffix',
                } = numberThreshold;
                const translatedText =
                    (translate && translate(dataStore.translations, text)) ||
                    text;

                if (displayPosition === 'suffix') {
                    return `${formattedNumber}${separator}${translatedText}`;
                }
                if (displayPosition === 'prefix') {
                    return `${translatedText}${separator}${formattedNumber}`;
                }
            }
            return formattedNumber;
        } catch (e) {
            console.warn(e);
            return value;
        }
    }
    return value;
};

export const formatDateTime = (formatDateOptions: FormatDateOpitons) => {
    let { value } = formatDateOptions;
    const { locale, options, dataStore } = formatDateOptions;
    // Cleans up dates coming in with a format of YYYY-MM-DD HH:mm:SS, it will change them to
    // YYYY-MM-DDTHH:mm:SS. This is required for how IE11 handles value format for the new Date(value).
    const dateRegex = new RegExp(
        /(\d){4}-(\d){2}-(\d){2} (\d){2}\:(\d){2}\:(\d){2}/
    );
    if (typeof value === 'string' && dateRegex.test(value)) {
        value = value.replace(' ', 'T');
    }

    const date = new Date(value);
    const isValidDate = isDate(date) && !isNaN(date.getTime());

    if (isValidDate) {
        try {
            return intlInstanceCache
                .getDateTime({ locale, options, dataStore })
                .format(date);
        } catch (e) {
            console.warn(e);
            return value;
        }
    }

    return value;
};

const formatter = (formatOptions: FormatterOptions) => {
    const { type, locale, options, value, currency, dataStore } = formatOptions;
    if (type === 'number') {
        return formatNumber({
            locale,
            options,
            dataStore,
            value: value as number,
        });
    }

    if (type === 'currency') {
        (options as Intl.NumberFormatOptions).currency = currency;
        return formatNumber({
            locale,
            options,
            dataStore,
            value: value as number,
        });
    }

    if (type === 'date') {
        return formatDateTime({
            locale,
            options,
            dataStore,
            value: value.toString(),
        });
    }

    return value;
};

export default formatter;



// WEBPACK FOOTER //
// ./src/utils/formatter.ts